import { computed, ref, type Ref } from 'vue';
import { toastError, toastSuccess, useCustomToast, formatDateForApi } from '@/shared/lib';
import type { DocumentRevision } from '@/entities/Document';
import updateInternalConfigApi, {
  type UpdateInternalConfigPayload,
} from '../api/updateInternalConfig.api';
import updateExternalConfigApi, {
  type UpdateExternalConfigPayload,
} from '../api/updateExternalConfig.api';
import type { EditModalFormData } from './useDocumentEditModal.types';
import { EXTERNAL_DOCUMENT_NAME_PREFIX } from './const';
import {
  documentEditModalCrossFieldsBlockSave,
  getDocumentEditModalCrossFieldValidationError,
  getEditModalNotificationCounts,
  useDocumentEditModalPositionCollaboratorOptions,
} from './useDocumentEditModalShared';

const buildExternalDocumentNameForPayload = (name: string | undefined): string | undefined => {
  if (name === undefined) return undefined;
  const body = name.startsWith(EXTERNAL_DOCUMENT_NAME_PREFIX)
    ? name.slice(EXTERNAL_DOCUMENT_NAME_PREFIX.length)
    : name;
  return `${EXTERNAL_DOCUMENT_NAME_PREFIX}${body}`;
};

interface DocumentEditModalProps {
  title: string;
  revision: DocumentRevision | null;
}

type DocumentEditModalVariant = 'internal' | 'external';

function appendValidityEditModalPayloadFields(
  payload: UpdateInternalConfigPayload | UpdateExternalConfigPayload,
  formData: EditModalFormData,
  toIsoDateString: (dateValue: unknown) => string | null,
  notificationUnchanged: boolean
): void {
  payload.hasValidity = formData.hasValidity === 'true';

  if (formData.hasValidity === 'true') {
    if (formData.validityDate) {
      const isoDate = toIsoDateString(formData.validityDate);
      if (isoDate) payload.validityDate = isoDate;
    }
    if (!notificationUnchanged) {
      payload.notificationDates = (formData.notificationDates ?? [])
        .map((d) => toIsoDateString(d))
        .filter((d): d is string => d !== null);
      payload.notificationMemberIds = formData.notificationMemberIds ?? [];
    }
  } else if (formData.hasValidity === 'false') {
    payload.validityDate = null;
    payload.notificationDates = [];
    payload.notificationMemberIds = [];
  }
}

function appendEditedByAndConfiguredAtEditModalPayloadFields(
  payload: UpdateInternalConfigPayload | UpdateExternalConfigPayload,
  formData: EditModalFormData,
  toIsoDateString: (dateValue: unknown) => string | null
): void {
  const collaboratorId = Number(formData.collaboratorId);
  const positionId = Number(formData.positionId);
  if (collaboratorId) payload.editedByUserId = collaboratorId;
  if (positionId) payload.editedByPositionId = positionId;

  if (formData.configurationDate) {
    const isoDate = toIsoDateString(formData.configurationDate);
    if (isoDate) payload.configuredAt = isoDate;
  }
}

function appendPhysicalLocationsEditModalPayloadFields(
  payload: UpdateInternalConfigPayload | UpdateExternalConfigPayload,
  formData: EditModalFormData
): void {
  const htmRows = formData.htmRows ?? [];
  const validLocations = htmRows.filter((r) => r.local && Number(r.local) > 0);
  if (validLocations.length > 0) {
    payload.physicalLocations = validLocations.map((r) => ({
      locationId: Number(r.local),
      quantity: r.copies,
    }));
  } else if (formData.format === 'EL') {
    payload.physicalLocations = [];
  }
}

function appendSharedEditModalPayloadFields(
  payload: UpdateInternalConfigPayload | UpdateExternalConfigPayload,
  formData: EditModalFormData,
  toIsoDateString: (dateValue: unknown) => string | null,
  notificationUnchanged: boolean
): void {
  if (formData.format) payload.format = formData.format;

  appendEditedByAndConfiguredAtEditModalPayloadFields(payload, formData, toIsoDateString);
  appendValidityEditModalPayloadFields(payload, formData, toIsoDateString, notificationUnchanged);
  appendPhysicalLocationsEditModalPayloadFields(payload, formData);
}

function useDocumentEditModalCore(
  variant: DocumentEditModalVariant,
  props: DocumentEditModalProps,
  emit: (event: 'saved') => void,
  formData: EditModalFormData,
  notifiedUsersCount: Ref<number>,
  notificationUnchanged: Ref<boolean>
) {
  const toast = useCustomToast();

  const { positionsResponse, positionOptions } =
    useDocumentEditModalPositionCollaboratorOptions(props, formData);

  const isDatePickerOpen = ref(false);

  const openDatePicker = () => {
    isDatePickerOpen.value = true;
  };

  const formatDisplayDate = (date: Date | null) => {
    if (!date) return 'Selecione uma data';
    return date.toLocaleDateString('pt-BR');
  };

  const toIsoDateString = (dateValue: unknown): string | null => {
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    if (dateValue instanceof Date) {
      return formatDateForApi(dateValue);
    }
    if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      const parsed = new Date(dateValue);
      return Number.isNaN(parsed.getTime()) ? null : formatDateForApi(parsed);
    }
    return null;
  };

  const isSelectedPositionValid = (): boolean => {
    if (!positionsResponse.value.success || positionOptions.value.length === 0) return true;
    return positionOptions.value.some((option) => String(option.id) === String(formData.positionId));
  };

  const canSave = computed(() => {
    if (!isSelectedPositionValid()) return false;
    if (formData.hasValidity === 'true' && !formData.validityDate) return false;
    const { memberCount, dateCount } = getEditModalNotificationCounts(
      formData,
      notifiedUsersCount.value
    );
    if (documentEditModalCrossFieldsBlockSave(formData, memberCount, dateCount, variant)) {
      return false;
    }
    return true;
  });

  const getValidationError = (): string | null => {
    if (!isSelectedPositionValid()) {
      return 'Selecione um cargo.';
    }
    if (formData.hasValidity === 'true' && !formData.validityDate) {
      return 'Data de vencimento é obrigatória quando o documento tem validade.';
    }
    const { memberCount, dateCount } = getEditModalNotificationCounts(
      formData,
      notifiedUsersCount.value
    );
    return getDocumentEditModalCrossFieldValidationError(formData, memberCount, dateCount, variant);
  };

  const buildInternalPayload = (): UpdateInternalConfigPayload => {
    const payload: UpdateInternalConfigPayload = {};

    if (formData.name !== undefined) payload.name = formData.name;

    if (formData.status === 'vigente') payload.status = 'CURRENT';
    else if (formData.status === 'inativo') payload.status = 'INACTIVE';

    if (formData.includeInMasterList !== undefined) {
      payload.includeInMasterList =
        formData.includeInMasterList === true || formData.includeInMasterList === 'true';
    }

    appendSharedEditModalPayloadFields(payload, formData, toIsoDateString, notificationUnchanged.value);
    return payload;
  };

  const buildExternalPayload = (): UpdateExternalConfigPayload => {
    const payload: UpdateExternalConfigPayload = {};

    if (formData.name !== undefined) {
      payload.name = buildExternalDocumentNameForPayload(formData.name);
    }

    if (formData.status === 'vigente') payload.status = 'CURRENT';
    else if (formData.status === 'revogado') payload.status = 'OBSOLETE';

    if (formData.includeInMasterList !== undefined) {
      payload.includeInMasterList =
        formData.includeInMasterList === true || formData.includeInMasterList === 'true';
    }

    if (formData.hasRevision === 'true') {
      payload.externalRevision = formData.externalRevision ?? '';
    } else if (formData.hasRevision === 'false') {
      payload.externalRevision = '';
    } else if (formData.externalRevision !== undefined) {
      payload.externalRevision = formData.externalRevision;
    }

    appendSharedEditModalPayloadFields(payload, formData, toIsoDateString, notificationUnchanged.value);
    return payload;
  };

  const handleSave = async () => {
    if (!props.revision?.id) return;

    const validationError = getValidationError();
    if (validationError) {
      toastError(toast, validationError);
      return;
    }

    try {
      if (variant === 'internal') {
        await updateInternalConfigApi.updateInternalConfig(
          props.revision.id,
          buildInternalPayload()
        );
      } else {
        await updateExternalConfigApi.updateExternalConfig(
          props.revision.id,
          buildExternalPayload()
        );
      }
      toastSuccess(toast, 'Edição salva com sucesso!');
      emit('saved');
    } catch (error) {
      const apiMessage = (error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toastError(toast, apiMessage ?? 'Erro ao salvar edição.');
    }
  };

  return {
    positionOptions,
    positionsResponse,
    isDatePickerOpen,
    openDatePicker,
    formatDisplayDate,
    handleSave,
    canSave,
  };
}

export default function useDocumentEditModal(
  props: DocumentEditModalProps,
  emit: (event: 'saved') => void,
  formData: EditModalFormData,
  notifiedUsersCount: Ref<number> = ref(0),
  notificationUnchanged: Ref<boolean> = ref(false)
) {
  return useDocumentEditModalCore(
    'internal',
    props,
    emit,
    formData,
    notifiedUsersCount,
    notificationUnchanged
  );
}

export function useExternalDocumentEditModal(
  props: DocumentEditModalProps,
  emit: (event: 'saved') => void,
  formData: EditModalFormData,
  notifiedUsersCount: Ref<number> = ref(0),
  notificationUnchanged: Ref<boolean> = ref(false)
) {
  return useDocumentEditModalCore(
    'external',
    props,
    emit,
    formData,
    notifiedUsersCount,
    notificationUnchanged
  );
}
